import React from 'react';

import {
  Button,
  Divider,
  Group,
  Input,
  LoadingOverlay,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  TextInput,
  Textarea,
  Text,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure, useShallowEffect } from '@mantine/hooks';
import ColorInput from 'components/ColorInput';
import Drawer from 'components/Drawer';
import ExpandableSection from 'components/ExpandableSection/ExpandableSection';
import LocationAutoComplete, {
  LocationAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/LocationAutoComplete';
import StaffMultiSelectAutoComplete from 'components/form-fields/auto-complete-fields/StaffMultiSelectAutoComplete';
import MoneyField from 'components/MoneyField';
import { useAuth } from 'context/auth-context';
import { useLocalization } from 'context/localization-context';
import useAppointmentType from 'hooks/appointmentType/useAppointmentType';
import useCreateAppointmentType from 'hooks/appointmentType/useCreateAppointmentType';
import useUpdateAppointmentType from 'hooks/appointmentType/useUpdateAppointmentType';
import useGym from 'hooks/gym/useGym';
import { TimeUnitEnum } from 'types/enums';
import { AppointmentTypeSelfBookingSettings, AppointmentTypeWrite } from 'types/generated';
import { setMaxLateCancellationIntervalCount } from 'utils/functions';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

const timeUnitOptions = [
  { value: TimeUnitEnum.MINUTE, label: `Min(s)` },
  { value: TimeUnitEnum.HOUR, label: `Hour(s)` },
  { value: TimeUnitEnum.DAY, label: `Day(s)` },
];

const selfBookingIntervalMinuteOptions = [
  { value: '30', label: '30 minutes' },
  { value: '45', label: '45 minutes' },
  { value: '60', label: '60 minutes' },
  { value: '90', label: '90 minutes' },
];

interface FormValues {
  name: string;
  appointmentTypeId: string;
  description: string | null;
  duration: number;
  color: string;
  cancellationSettings: {
    useLateCancelFee: boolean;
    useNoShowFee: boolean;
    allowLateCancellation: boolean;
    cancellationInterval: 'minute' | 'hour' | 'day';
    cancellationIntervalCount: number;
    cancellationFee: number;
    cancellationCreditDeduction: boolean;
    noShowFee: number;
    noShowCreditDeduction: boolean;
  };
  selfBookingSettings: {
    allowSelfBooking: boolean;
    // Mantine Select only accepts string values — kept as string here, converted to number on submit
    intervalMinutes: '30' | '45' | '60' | '90' | null;
    minimumBookingNoticeInterval: 'minute' | 'hour' | 'day' | null;
    minimumBookingNoticeIntervalCount: number | null;
  };
  selfBookingCoaches: string[];
  location: LocationAutoCompleteValue;
}

interface IDrawerAppointmentTypeFormProps {
  formId: string;
  title: JSX.Element;
  opened: boolean;
  closeHandler: () => void;
  appointmentTypeId?: string;
  type: 'create' | 'edit';
  permission?: boolean;
}

const DrawerAppointmentTypeForm = ({
  formId,
  title,
  opened,
  closeHandler,
  appointmentTypeId,
  type,
  permission = true,
}: IDrawerAppointmentTypeFormProps): JSX.Element => {
  const { country } = useLocalization();
  const { session } = useAuth();
  const { data: gymData } = useGym(session?.gym?.gymId);

  const createMutation = useCreateAppointmentType();
  const updateMutation = useUpdateAppointmentType();
  const { data, isLoading, isFetching, isSuccess } = useAppointmentType(appointmentTypeId);

  const [isAdditionalSettingsOpened, { toggle: toggleIsAdditionalSettingsOpened }] = useDisclosure(false);
  const [isSelfBookingSettingsOpened, { toggle: toggleIsSelfBookingSettingsOpened }] = useDisclosure(false);

  const getCancellationSettingsValue = (): FormValues['cancellationSettings'] => {
    const initAppointmentReservationWindowCancelMinutes = appointmentTypeId
      ? data?.data?.cancellationSettings
      : gymData?.data?.eventSettings?.appointments;

    return {
      useLateCancelFee: initAppointmentReservationWindowCancelMinutes?.cancellationFee !== 0,
      useNoShowFee: initAppointmentReservationWindowCancelMinutes?.noShowFee !== 0,
      allowLateCancellation: initAppointmentReservationWindowCancelMinutes?.allowLateCancellation ?? true,
      cancellationInterval: initAppointmentReservationWindowCancelMinutes?.cancellationInterval ?? 'hour',
      cancellationIntervalCount:
        initAppointmentReservationWindowCancelMinutes?.cancellationIntervalCount ?? 0,
      cancellationFee: initAppointmentReservationWindowCancelMinutes?.cancellationFee ?? 0,
      cancellationCreditDeduction:
        initAppointmentReservationWindowCancelMinutes?.cancellationCreditDeduction ?? true,
      noShowFee: initAppointmentReservationWindowCancelMinutes?.noShowFee ?? 0,
      noShowCreditDeduction: initAppointmentReservationWindowCancelMinutes?.noShowCreditDeduction ?? false,
    };
  };

  const getSelfBookingSettings = (): FormValues['selfBookingSettings'] => {
    const initSelfBookingSettings = data?.data?.selfBookingSettings;

    return {
      allowSelfBooking: initSelfBookingSettings?.allowSelfBooking || false,
      intervalMinutes: initSelfBookingSettings?.intervalMinutes
        ? (String(
            initSelfBookingSettings.intervalMinutes,
          ) as FormValues['selfBookingSettings']['intervalMinutes'])
        : null,
      minimumBookingNoticeInterval: initSelfBookingSettings?.minimumBookingNoticeInterval || 'minute',
      minimumBookingNoticeIntervalCount: initSelfBookingSettings?.minimumBookingNoticeIntervalCount || 0,
    };
  };

  const form = useForm<FormValues>({
    initialValues: {
      appointmentTypeId: '',
      name: '',
      description: '',
      duration: 60,
      color: '',
      cancellationSettings: getCancellationSettingsValue(),
      selfBookingSettings: {
        allowSelfBooking: false,
        intervalMinutes: null,
        minimumBookingNoticeInterval: 'minute',
        minimumBookingNoticeIntervalCount: 0,
      },
      selfBookingCoaches: [],
      location: {
        locationId: '',
        value: '',
        label: '',
      },
    },

    validate: {
      color: value => (value && value.length === 7 ? null : 'You must provide a valid color'),
      description: value =>
        value && value.length > 255 ? 'Description must be less than 255 characters' : null,
      duration: value => (!value ? 'Duration must be provided' : null),
      selfBookingCoaches: (value, values) => {
        if (values.selfBookingSettings.allowSelfBooking && (!value || value.length === 0)) {
          return 'At least one coach must be selected when self-booking is enabled';
        }
        return null;
      },
      selfBookingSettings: {
        intervalMinutes: (value, values) => {
          if (values.selfBookingSettings.allowSelfBooking) {
            if (!value) {
              return 'Meeting interval is required when self-booking is enabled';
            }
            if (Number(value) < values.duration) {
              return 'Interval must be greater than or equal to the appointment duration';
            }
          }
          return null;
        },
      },
    },
  });

  const handleCloseAndReset = (): void => {
    closeHandler();
    form.reset();
  };

  const handleLocationChange = (item: LocationAutoCompleteValue | null): void => {
    const selectedLocation = {
      locationId: item ? item.locationId : '',
      value: item ? item.value : '',
      label: item ? item.label : '',
    };
    form.setFieldValue('location', selectedLocation);
  };

  const handleSubmit = (values: FormValues): void => {
    const { errorNotification, successNotification } = notificationHandler();

    const payload: AppointmentTypeWrite = {
      ...values,
      cancellationSettings: {
        allowLateCancellation: values.cancellationSettings.allowLateCancellation,
        cancellationInterval: values.cancellationSettings.cancellationInterval,
        cancellationIntervalCount: values.cancellationSettings.cancellationIntervalCount,
        cancellationFee:
          values.cancellationSettings.useLateCancelFee && values.cancellationSettings.allowLateCancellation
            ? values.cancellationSettings.cancellationFee
            : 0,
        cancellationCreditDeduction: values.cancellationSettings.allowLateCancellation
          ? values.cancellationSettings.cancellationCreditDeduction
          : false,
        noShowFee: values.cancellationSettings.useNoShowFee ? values.cancellationSettings.noShowFee : 0,
        noShowCreditDeduction: values.cancellationSettings.noShowCreditDeduction,
      },
      selfBookingSettings: {
        allowSelfBooking: values.selfBookingSettings.allowSelfBooking,
        intervalMinutes: values.selfBookingSettings.intervalMinutes
          ? (Number(
              values.selfBookingSettings.intervalMinutes,
            ) as AppointmentTypeSelfBookingSettings['intervalMinutes'])
          : null,
        minimumBookingNoticeInterval: values.selfBookingSettings.minimumBookingNoticeInterval || 'minute',
        minimumBookingNoticeIntervalCount: values.selfBookingSettings.minimumBookingNoticeIntervalCount
          ? Number(values.selfBookingSettings.minimumBookingNoticeIntervalCount)
          : 0,
      },
      selfBookingCoaches: values.selfBookingCoaches || [],
      location: values.location?.locationId || null,
    };

    if (type === 'edit') {
      updateMutation.mutate(payload, {
        onError: error => {
          errorNotification('Appointment Type update failed', error.response?.data.message);
        },
        onSuccess: response => {
          successNotification(
            'Appointment Type update success',
            `${response.data.name} successfully updated.`,
          );
          handleCloseAndReset();
        },
      });
    } else {
      createMutation.mutate(
        {
          ...payload,
          appointmentTypeId: uuidv4(),
        },
        {
          onError: error => {
            errorNotification('Appointment Type creation failed', error.response?.data.message);
          },
          onSuccess: response => {
            successNotification(
              'Appointment Type creation success',
              `${response.data.name} successfully created.`,
            );
            handleCloseAndReset();
          },
        },
      );
    }
  };

  useShallowEffect(() => {
    if (type === 'edit' && data?.data && isSuccess) {
      form.setValues({
        ...data.data,
        description: data.data.description || '',
        cancellationSettings: getCancellationSettingsValue(),
        selfBookingSettings: getSelfBookingSettings(),
        selfBookingCoaches: data.data.selfBookingCoaches?.map((coach: any) => coach.staffId) || [],
        location: {
          locationId: data.data.location?.locationId || '',
          value: data.data.location?.locationId || '',
          label: data.data.location?.name || '',
        },
      });
    }
  }, [data]);

  useShallowEffect(() => {
    if (type === 'create' && gymData?.data) {
      form.setValues({
        ...gymData?.data,
        cancellationSettings: getCancellationSettingsValue(),
      });
    }
  }, [gymData?.data]);

  return (
    <Drawer
      opened={opened}
      onClose={handleCloseAndReset}
      size={550}
      title={title}
      closeOnClickOutside
      footer={
        <Group position='right'>
          <Button variant='subtle' onClick={handleCloseAndReset}>
            Cancel
          </Button>
          <Button
            disabled={!permission}
            form={formId}
            type='submit'
            loading={createMutation.isLoading || updateMutation.isLoading || isLoading || isFetching}
            data-cy='saveAppButton'
          >
            Save
          </Button>
        </Group>
      }
    >
      <LoadingOverlay visible={type === 'edit' && (isLoading || isFetching)} />

      <form id={formId} onSubmit={form.onSubmit(handleSubmit)}>
        <Stack spacing='xs'>
          <Group grow align='top'>
            <TextInput
              {...form.getInputProps('name')}
              data-cy='appNameInput'
              label='Name'
              placeholder='Enter appointment name'
              required
              disabled={!permission}
            />
            <ColorInput
              data-cy='colorInput'
              disabled={!permission}
              label='Color'
              placeholder='Set color'
              required
              {...form.getInputProps('color')}
            />
          </Group>

          <Group grow align='top'>
            <NumberInput
              disabled={!permission}
              {...form.getInputProps('duration')}
              label='Duration in minutes'
              placeholder='Set duration'
              min={15}
              required
              data-cy='durationInput'
            />
            <LocationAutoComplete
              label='Location'
              placeholder='Select a location'
              value={form.values.location}
              onChange={handleLocationChange}
              error={form.errors.location}
              data-cy='locationInput'
              disabled={!permission}
            />
          </Group>

          <Textarea
            label='Description'
            placeholder='Description For This Appointment'
            {...form.getInputProps('description')}
            disabled={!permission}
          />

          <Divider mt='lg' mb={0} mx='-xl' />

          <ExpandableSection
            title='Additional Settings'
            opened={isAdditionalSettingsOpened}
            handleToggle={toggleIsAdditionalSettingsOpened}
          >
            <Stack spacing='sm' px='md'>
              <Input.Wrapper>
                <SimpleGrid cols={1}>
                  <Switch
                    my='xs'
                    disabled={!permission}
                    label='Allow late cancellations'
                    checked={form.values.cancellationSettings.allowLateCancellation}
                    {...form.getInputProps('cancellationSettings.allowLateCancellation')}
                  />
                </SimpleGrid>
              </Input.Wrapper>
              <Input.Wrapper
                label={
                  form.values.cancellationSettings.allowLateCancellation
                    ? 'Time before the start of an appointment when a cancellation is considered late'
                    : 'Time before the start of an appointment when cancellations are no longer allowed'
                }
              >
                <SimpleGrid cols={2}>
                  <NumberInput
                    min={0}
                    disabled={!permission}
                    max={setMaxLateCancellationIntervalCount(
                      form.values.cancellationSettings.cancellationInterval,
                    )}
                    {...form.getInputProps('cancellationSettings.cancellationIntervalCount')}
                    required
                    data-cy='appointmentTypeLateCancel'
                  />
                  <Select
                    data={timeUnitOptions}
                    disabled={!permission}
                    {...form.getInputProps('cancellationSettings.cancellationInterval')}
                  />
                </SimpleGrid>
              </Input.Wrapper>

              {form.values.cancellationSettings.allowLateCancellation && (
                <>
                  <Group noWrap>
                    <Switch
                      my='xs'
                      disabled={!permission}
                      checked={form.values.cancellationSettings.useLateCancelFee}
                      {...form.getInputProps('cancellationSettings.useLateCancelFee')}
                    />
                    <Text size='sm'>Auto-Charge Fee for late cancellation</Text>
                    {form.values.cancellationSettings.useLateCancelFee && (
                      <MoneyField
                        country={country}
                        min={0}
                        disabled={!permission}
                        {...form.getInputProps('cancellationSettings.cancellationFee')}
                        data-cy='appointmentTypeLateCancelAutoCharge'
                      />
                    )}
                  </Group>
                  <Input.Wrapper>
                    <SimpleGrid cols={1}>
                      <Switch
                        my='xs'
                        disabled={!permission}
                        label='Deduction of credit for Late Cancellations'
                        checked={form.values.cancellationSettings.cancellationCreditDeduction}
                        {...form.getInputProps('cancellationSettings.cancellationCreditDeduction')}
                        data-cy='appointmentTypeLateCancelDeductionCredit'
                      />
                    </SimpleGrid>
                  </Input.Wrapper>
                </>
              )}

              <Divider my='md' />

              <Group noWrap>
                <Switch
                  my='xs'
                  disabled={!permission}
                  checked={form.values.cancellationSettings.useNoShowFee}
                  {...form.getInputProps('cancellationSettings.useNoShowFee')}
                />
                <Text size='sm'>Auto-Charge Fee for No Shows</Text>
                {form.values.cancellationSettings.useNoShowFee && (
                  <MoneyField
                    country={country}
                    min={0}
                    disabled={!permission}
                    {...form.getInputProps('cancellationSettings.noShowFee')}
                    data-cy='appointmentTypeAutoChargeNoShow'
                  />
                )}
              </Group>
              <Input.Wrapper>
                <SimpleGrid cols={1}>
                  <Switch
                    my='xs'
                    disabled={!permission}
                    label='Deduction of credit for No Shows'
                    checked={form.values.cancellationSettings.noShowCreditDeduction}
                    {...form.getInputProps('cancellationSettings.noShowCreditDeduction')}
                    data-cy='appointmentTypeDeductionCreditNoShow'
                  />
                </SimpleGrid>
              </Input.Wrapper>
            </Stack>
          </ExpandableSection>

          <Divider mt='lg' mb={0} mx='-xl' />

          <ExpandableSection
            title='Self-booking Settings'
            opened={isSelfBookingSettingsOpened}
            handleToggle={toggleIsSelfBookingSettingsOpened}
          >
            <Stack spacing='sm' px='md'>
              <Group noWrap>
                <Switch
                  my='xs'
                  disabled={!permission}
                  checked={form.values.selfBookingSettings.allowSelfBooking}
                  {...form.getInputProps('selfBookingSettings.allowSelfBooking')}
                />
                <Text size='sm'>Turn On</Text>
              </Group>

              {form.values.selfBookingSettings.allowSelfBooking && (
                <>
                  <StaffMultiSelectAutoComplete
                    label='Add coaches'
                    placeholder='Select coaches'
                    disabled={!permission}
                    isCoach={true}
                    showNoStaff={false}
                    {...form.getInputProps('selfBookingCoaches')}
                    required
                  />

                  <Select
                    label='Select Meeting Interval'
                    placeholder='Choose interval'
                    disabled={!permission}
                    data={selfBookingIntervalMinuteOptions}
                    required
                    {...form.getInputProps('selfBookingSettings.intervalMinutes')}
                  />

                  <Input.Wrapper label='Minimum scheduling notice' required>
                    <SimpleGrid cols={2}>
                      <NumberInput
                        min={0}
                        disabled={!permission}
                        placeholder='0'
                        value={form.values.selfBookingSettings.minimumBookingNoticeIntervalCount || 0}
                        onChange={value =>
                          form.setFieldValue(
                            'selfBookingSettings.minimumBookingNoticeIntervalCount',
                            value || 0,
                          )
                        }
                        error={form.errors['selfBookingSettings.minimumBookingNoticeIntervalCount']}
                        required
                      />
                      <Select
                        data={timeUnitOptions}
                        disabled={!permission}
                        {...form.getInputProps('selfBookingSettings.minimumBookingNoticeInterval')}
                      />
                    </SimpleGrid>
                  </Input.Wrapper>
                </>
              )}
            </Stack>
          </ExpandableSection>
        </Stack>
      </form>
    </Drawer>
  );
};

export default DrawerAppointmentTypeForm;
